import { Component, EventEmitter, OnInit, Output } from '@angular/core';
import { FormBuilder, Validators} from '@angular/forms';
import { Router } from '@angular/router'; 
import { MatSnackBar } from '@angular/material/snack-bar';
import { emailValidator } from 'src/app/theme/utils/app-validators';
import { keyTypesEnum, NavigatorService } from 'src/app/services/vendor/navigator.service';
import { Address, AutomationTag, CompanyProfile, GroupPermissions, Insurance, License, PlatformInvoice, StripePaymentDetails } from 'src/app/data-models/models';
import { Observable, Subscription } from 'rxjs';
import { ImageHandlerService } from 'src/app/services/vendor/image-handler.service';
import { CompanyTiers, PlatformService } from 'src/app/services/vendor/platform.service';
import { StepperOrientation } from '@angular/cdk/stepper';
import { BreakpointObserver } from '@angular/cdk/layout';
import { map } from 'rxjs/operators';
import { PaymentHandlerService } from 'src/app/services/vendor/payment-handler.service';
import { environment } from 'src/environments/environment';
import { TwilioHandlerService } from 'src/app/services/vendor/twilio-handler.service';
import { TradesClassificationTypes } from 'src/app/enums/tradesClassificationTypes';
import { AiCopilotService } from 'src/app/services/vendor/ai-copilot.service';


@Component({
  selector: 'app-register-company',
  templateUrl: './register-company.component.html',
  styleUrls: ['./register-company.component.scss']
})
export class RegisterCompanyComponent implements OnInit {
  @Output('register') registerEmitter: EventEmitter<any> = new EventEmitter<any>();

  public classificationTagOptions = Object.values(TradesClassificationTypes);
  public licenses: Array<License> = [];
  public insurances: Array<Insurance> = [];

  private initialWorkOrderStatusTags: Array<AutomationTag> = [
    {
      "id": "1fc896324f52a9c5a86808458707228b29df",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "On Our Way",
      "tagType": 0,
      "baseType": 2,
      "automation": null,
      "border": "#53aee0",
      "background": "#53aee0",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "e14f9ed9fb7ec277ec911c644466bb8a4bb6",
          "allowEmail": true,
          "allowSms": true,
          "emailMsg": "A technician is on the way. They'll be there soon!",
          "smsMsg": "A technician is on the way. He'll be there soon!"
      },
      "description": "A technician is on their way!"
    },
    {
      "id": "2b21f833baa2514c3235e8cb6a08bae64b9d",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "In Progress",
      "tagType": 0,
      "baseType": 3,
      "automation": null,
      "border": "#2ace60",
      "background": "#2ace60",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "1c8c61fb4f40e074464fa6003fa999e7888e",
          "allowEmail": true,
          "allowSms": true,
          "emailMsg": "We're actively working on your repair. We'll be done as soon as possible and thank you for choosing us!",
          "smsMsg": "A technician is on the way. He'll be there soon!"
      },
      "description": "Work on the ticket has begun"
    },
    {
      "id": "56e3303e3cbe6158685422ac8e46732f3edc",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Pending Payment",
      "tagType": 0,
      "baseType": 5,
      "automation": null,
      "border": "#ed5050",
      "background": "#ed5050",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "e42603bfa01edc4a60175e37b3d6405751e4",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "An invoice is available and awaiting your final payment. Please contact us if you have any questions.",
          "smsMsg": ""
      },
      "description": "Waiting for the customer to pay their bill."
    },
    {
      "id": "19490ab5e53440dd7e79e58ac7f8751a586c",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Completed",
      "tagType": 0,
      "baseType": 6,
      "automation": null,
      "border": "#4aa4e6",
      "background": "#4aa4e6",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "7decd3d8f1c368984d5ff2f183e25bbbadbc",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "Thank you for choosing us!",
          "smsMsg": ""
      },
      "description": "Job Completed"     
    },
    {
      "id": "33d39a1bc9d4d94172a385c19dd7b8691d64",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Standby",
      "tagType": 0,
      "baseType": 0,
      "automation": null,
      "border": "#bababa",
      "background": "#ffffff",
      "color": "#000000",
      "extNotificationAutomation": {
          "id": "92820c0263c34a3c961951af9d660e58753a",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "Waiting to start work",
          "smsMsg": ""
      },
      "description": "The job is on on standby and is waiting to be picked up. "
    },
    {
      "id": "b8ea089b50a17042f942628f323dae3e86aa",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Scheduled",
      "tagType": 0,
      "baseType": 8,
      "automation": null,
      "border": "#e67e14",
      "background": "#e67e14",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "d9c4fcc8571b2c48b562ec1375e31d5b9fcd",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "We've got you on the schedule, help is on the way!",
          "smsMsg": ""
      },
      "description": "The job has been scheduled!"
    },
    {
      "id": "d4d08663f9e3a1ed7c69f16a5cb852da1a97",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Active Work",
      "tagType": 0,
      "baseType": 4,
      "automation": null,
      "border": "#00d601",
      "background": "#00d601",
      "color": "#000000",
      "extNotificationAutomation": {
          "id": "bdb37fcb65542e748548ac2aeead729da374",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "We're actively working on your repair. We'll be done as soon as possible and thank you for choosing us!",
          "smsMsg": ""
      },
      "description": "Longer running work that may span multiple days."
    },
    {
      "id": "2ff52532c3866093fdd6b5959e0fb24f6c40",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Canceled",
      "tagType": 0,
      "baseType": 7,
      "automation": null,
      "border": "#000000",
      "background": "#0c0c0c",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "f38b720d7b1416c93eadcc2cdd1eee9f2092",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "You job has been canceled, thank you for letting us work with you!",
          "smsMsg": ""
      },
      "description": "The job was canceled for some reason."     
    },
    {
      "id": "f6577855de0d7e77e0b4a503c2468b9fe772",
      "companyId": "5bde0a9d2be9ec80c5b900a839ff0d43603e",
      "title": "Hold",
      "tagType": 0,
      "baseType": 1,
      "automation": null,
      "border": "#760707",
      "background": "#760707",
      "color": "#ffffff",
      "extNotificationAutomation": {
          "id": "9db2973f52181322a1d8e9eb4416f0d789a8",
          "allowEmail": true,
          "allowSms": false,
          "emailMsg": "Your job is currently on hold but we'll get right on it as soon as we can!",
          "smsMsg": ""
      },
      "description": "The job is being held up by other work or for unforeseen reasons."       
    }
  ];

  public basicInfoForm = this.formBuilder.group({
    company_name: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
    email: ['', Validators.compose([Validators.required, emailValidator])],
    phone: ['', Validators.required],
    classificationTags: [[], Validators.required], 
    desc: ['', Validators.required], 

    image: null,

    street: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
    city: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
    state: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
    zip: ['', Validators.compose([Validators.required, Validators.minLength(5)])]
  });

  public socialInfoForm = this.formBuilder.group({
    facebook: null,
    twitter: null,
    linkedin: null,
    instagram: null,
    website: null
  });

  public complianceForm = this.formBuilder.group({
    // ein: ['', Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)])],
    acceptTerms: [ false, Validators.requiredTrue],
    acceptESign: [ false, Validators.requiredTrue]
  });

  private keyTypes:any = keyTypesEnum;

  private imgSubscription: Subscription;
  public imgId: string = null;
  public image: any;
  public imageDeleteAction: boolean = false;

  private groupPermissionSubscriber: Subscription;
  public groupPermissions: Array<GroupPermissions> = [];
  public lockedGroupPermissions: Array<GroupPermissions> = [
  
    {
      id: "locked-all",
      group: "all",
      employeeIds: [ ],
      pages: JSON.parse( JSON.stringify(this.navigatorService.getPagePermissionsTemplate()) )
    },

    {
      id: "locked-admin",
      group: "admin",
      employeeIds: [ ],
      pages: JSON.parse( JSON.stringify(this.navigatorService.getPagePermissionsTemplate()) )
    },

    {
      id: "locked-technicians",
      group: "technicians",
      employeeIds: [ ],
      pages: JSON.parse( JSON.stringify(this.navigatorService.getPagePermissionsTemplate()) )
    }

  ];
  
  stepperOrientation: Observable<StepperOrientation>;


  private companyId: string = this.navigatorService.generateKey(this.keyTypes.COMPANY_ID);

  constructor(
    private platformService: PlatformService,
    private formBuilder: FormBuilder, 
    private aiCopilotService: AiCopilotService,
    private breakpointObserver: BreakpointObserver,
    private router:Router, 
    private snackBar: MatSnackBar, 
    private navigatorService: NavigatorService, 
    private imageHandler: ImageHandlerService,
    private paymentHandler: PaymentHandlerService,
    private twilioHandlerService: TwilioHandlerService) { }

  ngOnInit() {

    this.registerListeners();

    this.basicInfoForm = this.formBuilder.group({
      company_name: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
      email: ['', Validators.compose([Validators.required, emailValidator])],
      phone: ['', Validators.required],
      classificationTags: [[], Validators.required], 
      desc: ['', Validators.required], 

      image: null,

      street: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
      city: ['', Validators.compose([Validators.required, Validators.minLength(3)])],
      state: ['', Validators.compose([Validators.required, Validators.minLength(2)])],
      zip: ['', Validators.compose([Validators.required, Validators.minLength(5)])]
    });

    this.socialInfoForm = this.formBuilder.group({
      facebook: null,
      twitter: null,
      linkedin: null,
      instagram: null,
      website: null
    });

    this.complianceForm = this.formBuilder.group({
      // ein: ['', Validators.compose([Validators.required, Validators.minLength(9), Validators.maxLength(9)])],
      acceptTerms: [ false, Validators.requiredTrue],
      acceptESign: [ false, Validators.requiredTrue]
    });


  }

  ngOnDestroy() {

    if(this.imgSubscription) {
      this.imgSubscription.unsubscribe();
    }

    if(this.groupPermissionSubscriber) {
      this.groupPermissionSubscriber.unsubscribe();
    }

  }  

  private registerListeners(): void {

    this.stepperOrientation = this.breakpointObserver
    .observe('(min-width: 800px)')
    .pipe(map(({matches}) => (matches ? 'horizontal' : 'vertical')));

  }

  public openWindowURL(url: string): void {
    window.open("https://www.swarmoperative.com" + url);
  }

  public imgSubscriber(): void {

    this.imgSubscription = this.imageHandler.getFile(this.imgId).subscribe(img => {

      if(img) {
        this.image = img;

        console.log("Image: ", this.image);
      }

    });
    
  }

  public getImg(): void {
    if(this.imgId != undefined && this.imgId.length > 0) {
      this.imageHandler.getFile(this.imgId);
    }
  }

  public uploadImage(image: any, folder: string): string {
    let imgId: any = "";

    if(image != null && image != undefined && image.length > 0) {
        let file: File = image[0].file;
        let imgIdUnique: string = this.navigatorService.generateKey(this.keyTypes.IMAGE_ID);

        this.imageHandler.uploadFile(file, imgIdUnique, folder).then(data => {
          this.imgId = imgIdUnique;
          this.getImg();
        })

        imgId = imgIdUnique;
    }

    return imgId
  }

  public deleteImg(): void {
    this.basicInfoForm.controls["image"].setValue("");
    this.image = null;
    this.imageDeleteAction = true;
  }

  public createAnnualMemberInvoice(tier: string = CompanyTiers.STARTER ): void {
    const tiers = this.platformService.getTiers();
    const price: number = tiers[tier]?.price;
    const tierTitle: string = tiers[tier]?.title;



    let invoice: PlatformInvoice = {
      id: this.navigatorService.generateKey(),
      companyId: this.companyId,
      discountCode: null,
      discountPerc: 0,
      discountTotal: 0,
      quantity: 1,
      price: price,
      dueDate: new Date(),
      paid: null, // Waiving Member Fees
      desc: `Swarm Operative ${tierTitle} Monthly Member Fee`,
      annualMembership: true,
      tag: [],
      last_update: new Date(),
      created_date: new Date(),
      created_by: 'swarm_operative'
    };

    this.paymentHandler.createPlatformPaymentInvoice(invoice);

  }

  public memberFee(platformInvoice: PlatformInvoice): void {

    let invoice: StripePaymentDetails = {
      stripeAccount: '',
      paymentDetails: [
        {
          name: platformInvoice.desc,
          currency: "usd",
          quantity: platformInvoice.quantity,
      
          // tax_rates: number;
          amount: platformInvoice.price
        }
      ]
    };

    // this.paymentHandler.makePlatformStripePayment(invoice, this.companyId);

  }

  public updateLicenses(licenses: Array<License>) {
    if (!licenses) return;

    this.licenses = licenses;
  }

  public updateInsurances(insurances: Array<Insurance>) {
    if (!insurances) return;

    this.insurances = insurances;
  }

  public async onRegisterFormSubmit(): Promise<void> {

    if(this.basicInfoForm.valid && this.socialInfoForm.valid && this.complianceForm.valid) {
      let companyId: string = this.companyId;
      let imgId: string = "";

      if((this.basicInfoForm.get("image").value != null && this.basicInfoForm.get("image").value.length > 0) || this.imageDeleteAction) {
        imgId = this.uploadImage(this.basicInfoForm.get("image").value, companyId);
        this.imageDeleteAction = false;
      } else {
        imgId = this.imgId; 
      }

      let address: Address = {
        street: this.basicInfoForm.get("street").value,
        city: this.basicInfoForm.get("city").value,
        state: this.basicInfoForm.get("state").value,
        zip: this.basicInfoForm.get("zip").value
      }

      let company: CompanyProfile = {
        id: companyId,
        company_name: this.basicInfoForm.get("company_name").value,
        email: this.basicInfoForm.get("email").value,
        phone: this.basicInfoForm.get("phone").value,
        classificationTags: this.basicInfoForm.get('classificationTags').value, 
        desc: this.basicInfoForm.get('desc').value, 
        address: address,
        linkedin: this.socialInfoForm.get("linkedin").value,
        twitter: this.socialInfoForm.get("twitter").value,
        instagram: this.socialInfoForm.get("instagram").value,
        facebook: this.socialInfoForm.get("facebook").value,
        website: this.socialInfoForm.get("website").value,

        // ein: this.complianceForm.get("ein").value,
        ein: null,
        acceptTerms: (this.complianceForm.get("acceptTerms").value)? new Date() : null,
        acceptESign: (this.complianceForm.get("acceptESign").value)? new Date() : null,

        insurances: this.insurances,
        licenses: this.licenses,

        platformLicense: CompanyTiers?.TRIAL,
        subscriptionPeriod: new Date(),

        image: imgId,
        created_date: new Date()
      }

      const status: boolean = await this.navigatorService.registerNewCompany(company);

        if(!status) {
          this.snackBar.open('Failed to Register Company!', '×', { panelClass: 'failed', verticalPosition: 'top', duration: 3000 });
          return;
        }

        this.newCompanyAlert();

        await this.subscribeToGroupPermissions();
        await this.buildWorkOrderStatusTags();
        await this.aiCopilotService.purchasePhoneNumber(469);
        await this.aiCopilotService.upsertPilotModules();

        this.snackBar.open('Registration successfull!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 3000 });
        this.registerEmitter.emit();

        // this.navigatorService.logout();
        await this.aiCopilotService.loadPilotModules();
        await this.navigatorService.switchCompanyProfile(company.id);

        this.platformService.metaStartTrialEventTracker();

        this.router.navigate(['/login']);
    }
  }

  private newCompanyAlert(): void {

    let message: string = "New Company Registration: " + this.navigatorService.getCompanyName();
    let phone: string = environment?.adminPhone;

    this.twilioHandlerService.sendTextMessage(message, phone);

  }

// ==============================
//        Group Permissions
// ==============================

  private async subscribeToGroupPermissions(): Promise<void> {

    const groups: Array<GroupPermissions> = await this.navigatorService.getAsyncGroupPermissions()

    if(!groups) return;

    console.log("Returned Group Permissions: ", groups);
    this.groupPermissions = groups;
    this.loadLockedGroups();
    this.setDefaultPermissions();
      
  }

  private getGroupPermissions(): void {
    this.navigatorService.getGroupPermissions();
  }

  private loadLockedGroups(): void {

    this.lockedGroupPermissions.forEach((permission, permissionIndex) => {
      this.groupPermissions.push(permission);
    });

  }

  private async setDefaultPermissions(): Promise<void> {
    console.log("Setting Group Default Permissions: ", this.groupPermissions);

    for(let group of this.groupPermissions) {

      if(group.id == "locked-admin") {

        group.employeeIds.push(this.navigatorService.getProfileId());

        for(let page of group.pages) {

          page.permission.pageAccess = true;

          for(let section of page.permission.sections) {

            section.permissions.create = true;
            section.permissions.read = true;
            section.permissions.update = true;
            section.permissions.delete = true;

          }

        }

      } else {
        group.employeeIds.push(this.navigatorService.getProfileId());
      }

    }

    await this.saveGroup();
  }

  private async saveGroup(): Promise<void> {
    console.log("Saving Group Permissions: ", this.groupPermissions);
    const completed: boolean = await this.navigatorService.updateGroupPermissions(this.groupPermissions)

    if(completed) {
      this.snackBar.open('Groups Saved!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 4000 });
      await this.reloadGroupPermissions();
    }

  }


// ==============================
//     Work Order Status Tags
// ==============================

  private async buildWorkOrderStatusTags(): Promise<void> {

    this.initialWorkOrderStatusTags.forEach(async (tag: AutomationTag) => {
      
      await this.saveTag(tag);

    });

  }

  private async saveTag(tag: AutomationTag): Promise<void> {

    console.log("Save Tag: ", tag);

    const status: boolean = await this.navigatorService.updateAutomationTag(tag)

    if(status) {
      this.snackBar.open('Tag Saved Successfully!', '×', { panelClass: 'success', verticalPosition: 'top', duration: 3000 });
    } else {
      this.snackBar.open('Tag failed to save, please try again later!', '×', { panelClass: 'error', verticalPosition: 'top', duration: 3000 });
    }

  }

  private async reloadGroupPermissions(): Promise<void> {
    
    await this.platformService.refreshUserPermissions();

  }

}
